import { Formik } from 'formik';
import { Bookmark2 } from 'iconsax-react';

import { Textfield } from '@src/shared/fields/text-field';
import { PublicOrPrivateDropdown } from './public-or-private-dropdown';
import { Checkbox } from '@src/design-system/checkbox';
import { Label } from '@src/design-system/label';
import { Button } from '@src/design-system/button';
import { boolean, object, string } from 'yup';
import viewApi from '../services/view-api';
import { useTiclickTableContext } from '@src/design-system/molecules/ticlick-table/context/ticlick-table-context-hook';
import type { TView } from '@shared/types/view';
import { useSearchParams } from 'react-router-dom';
import type { RefObject } from 'react';
import { useTableStates } from '@src/design-system/molecules/ticlick-table/hooks/use-table-states';

export function CreateOrEditViewForm({
  view,
  closeRef,
}: {
  view?: TView;
  closeRef?: RefObject<HTMLElement | null>;
}) {
  const { mutateAsync: createView, isPending: isCreatePending } =
    viewApi.useCreate();
  const { mutateAsync: updateView, isPending: isUpdatePending } =
    viewApi.useUpdate();

  const { table } = useTiclickTableContext();
  const {
    tableFilters,
    columnOrder,
    columnVisibility,
    columnPinning,
    columnNames,
  } = useTableStates();

  const [, setSp] = useSearchParams();

  const { viewEntityType, viewEntityId } =
    table.options.meta?.identifyingData || {};

  const handleSaveViewSubmit = (values: {
    name: string;
    isDefault: boolean;
    isPublic: boolean;
  }) => {
    const data = {
      entity_type: viewEntityType,
      entity_id: viewEntityId,
      name: values.name,
      is_default: values.isDefault,
      is_public: values.isPublic,
      static_filters: tableFilters?.staticFilters,
      filters: tableFilters?.dynamicFilters?.map((d) => d.id) || [],
      custom_filters: tableFilters?.customFieldFilters?.map((d) => d.id) || [],
      visible_static_fields: {
        ordering: columnOrder,
        pinning: columnPinning,
        visibility: columnVisibility,
        names: columnNames,
      },
    };

    const onSuccess = async (newView: TView) => {
      setSp((prev) => ({ ...prev, view: newView.id }));
      closeRef?.current?.click?.();
    };

    if (view?.id) {
      updateView({ id: view.id, ...data }).then(onSuccess);
    } else {
      createView(data).then(onSuccess);
    }
  };

  return (
    <Formik
      onSubmit={handleSaveViewSubmit}
      initialValues={{
        name: view?.name || '',
        isDefault: !!view?.is_default,
        isPublic: !!view?.is_public,
      }}
      validationSchema={object().shape({
        name: string().required('View name is required'),
        isDefault: boolean(),
        isPublic: boolean(),
      })}
    >
      {({ values, setFieldValue, handleSubmit }) => (
        <form
          onSubmit={handleSubmit}
          className='space-y-4'
        >
          <PublicOrPrivateDropdown
            value={values.isPublic}
            onChange={(isPublic) => setFieldValue('isPublic', isPublic)}
          />

          <div className='space-y-1.5'>
            <Textfield
              name='name'
              size='sm'
              label='View Name'
              placeholder='e.g. Marketing view'
            />
          </div>

          <div className='flex gap-2 items-center'>
            <Checkbox
              checked={values.isDefault}
              onCheckedChange={(checked) => setFieldValue('isDefault', checked)}
              id='save-as-default'
            />
            <Label
              htmlFor='save-as-default'
              className='mb-0 text-primary-900 text-xs font-normal cursor-pointer select-none'
            >
              Set as default view
            </Label>
          </div>

          <Button
            type='submit'
            isLoading={isCreatePending || isUpdatePending}
            size='sm'
            className='w-full text-xs! [&_svg]:size-5 flex items-center justify-center gap-1.5'
          >
            <Bookmark2 size={14} />
            {view?.id ? 'Update' : 'Save View'}
          </Button>
        </form>
      )}
    </Formik>
  );
}
